/**
 * Where the API lives.
 *
 * By default the client talks to its own origin, which is what happens when the
 * Node server serves the built app itself (`npm start`, or the Docker image).
 *
 * Set `VITE_API_BASE` at build time to point the frontend somewhere else. That
 * covers two cases:
 *  - a static host serving only `client/dist`, with the API on another box;
 *  - a Capacitor build, where the app has no origin of its own at all.
 *
 * The server must list the frontend's origin in `INKSHARE_ORIGINS`, or the
 * browser will refuse the cross-origin calls.
 */

/**
 * Where a native build looks when nothing was set at build time.
 *
 * A Capacitor app runs from `https://localhost`, so a relative `/api` call goes
 * to the WebView itself and fails — the app installs, opens, and then cannot
 * sign in, with nothing useful on screen to say why. Forgetting the build flag
 * is easy and the symptom points nowhere near the cause, so a native build
 * falls back to the production server rather than to something that cannot
 * work. `VITE_API_BASE` still wins whenever it is set.
 */
const NATIVE_FALLBACK = 'https://inkshare.app';

const configured = (import.meta.env.VITE_API_BASE ?? '').trim().replace(/\/+$/, '');

/**
 * Resolved on first use rather than at import time: the Capacitor bridge is
 * injected into the page by the native shell, and this module could otherwise
 * be evaluated before it lands.
 */
let resolved: string | null = null;

function base(): string {
  if (resolved !== null) return resolved;

  let value = configured;
  if (!value) {
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    value = cap?.isNativePlatform?.() ? NATIVE_FALLBACK : '';
  }

  resolved = value;
  return value;
}

/** Absolute API origin, or '' when the API shares this origin. */
export const API_BASE = configured;

/** Build a full API URL. */
export const apiUrl = (path: string) => `${base()}/api${path}`;

/** WebSocket endpoint, derived from the API base so the two never disagree. */
export function socketUrl() {
  const origin = base();
  if (!origin) {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${window.location.host}/ws`;
  }
  return `${origin.replace(/^http/, 'ws')}/ws`;
}
